import React, { useState, useEffect, useCallback } from "react";
import api from "../../../utils/apiConfig";
import { useB2BAuth } from "../B2BShared";
import { useB2BTranslation } from "../B2BShared/hooks/useB2BTranslation";
import { useB2BDataLoadingState } from "../B2BShared/hooks/useB2BLoadingState";
import B2BBackButton from "../B2BShared/B2BBackButton";
import B2BProfileCompletionAlert from "../B2BShared/B2BProfileCompletionAlert/B2BProfileCompletionAlert";
import B2BDeleteProfileButton from "../B2BShared/B2BDeleteProfileButton/B2BDeleteProfileButton";
import B2BEditProfileButton from "../B2BShared/B2BEditProfileButton/B2BEditProfileButton";
import { translatePosition } from "../B2BShared/utils/translatePosition";
import "./B2BProfile.css";
import "../B2BShared/B2BButtonStyles/B2BButtonStyles.css";

interface ProfileData {
  _id?: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber?: string;
  countryCode?: string;
  position: string | string[];
  linkedin?: string;
  profilePhoto?: string | null;
  birthday?: string;
  cityOfBirth?: string;
  businesses?: any[];
  interestedIn?: string[];
}


const B2BProfile: React.FC = () => {
  const { tDashboard, tBusinessCategory, tDashboardExists } = useB2BTranslation();
  const { user } = useB2BAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const {
    isLoading,
    fetchData
  } = useB2BDataLoadingState();
  
  const loadProfile = useCallback(async () => {
    await fetchData(async () => {
      const data = await api.getProfile();
      const userData = data.data;
      setProfileData({
        ...userData,
        surname: userData.surname || "",
        interestedIn: userData.interestedIn || [],
      });
    }).catch(() => {
      if (user) {
        setProfileData({
          ...user,
          surname: user.surname || "",
          interestedIn: user.interestedIn || [],
        } as ProfileData);
      }
    });
  }, [fetchData, user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);




  if (isLoading) {
    return (
      <div className="b2b-profile-loading">
        <div className="b2b-profile-spinner" aria-hidden="true" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="b2b-profile-error">
        <h2>{tDashboard('profile.errorTitle')}</h2>
        <p>{tDashboard('profile.errorMessage')}</p>
        <B2BBackButton 
          to="/gcb/dashboard" 
          translationKey="profile.backToDashboard"
          variant="orange"
          size="medium"
        />
      </div>
    );
  }

  return (
    <div className="b2b-profile-container">
      {/* Header similar to dashboard events */}
      <div className="dashboard-profile-header">
        <B2BBackButton 
          to="/gcb/dashboard" 
          translationKey="profile.backToDashboard"
          variant="orange"
          size="medium"
        />
        <div className="profile-header-content">
          <h1>{profileData.name} {profileData.surname}</h1>
        </div>
      </div>

      {/* Profile Completion Alert */}
      <B2BProfileCompletionAlert />

      {/* Action Buttons Above Profile */}
      <div className="b2b-profile-actions-section">
        <B2BEditProfileButton
          to="/gcb/profile/edit"
          state={{
            editMode: true,
            profileData: profileData
          }}
          size="medium"
          variant="primary"
          showIcon={true}
        />
        <B2BDeleteProfileButton
          onSuccess={() => {
            // Handle success - user will be logged out automatically
          }}
          onError={(error) => {
            // Error handling - user will be logged out automatically
          }}
          size="medium"
          variant="primary"
          showIcon={true}
        />
      </div>

      {/* Profile Content - Order matches questionnaire: Business → Interests → Personal */}
      <div className="b2b-profile-content">
        <div className="profile-sections">
          
          {/* Business Information Section - First, matches questionnaire */}
          {profileData.businesses && profileData.businesses.length > 0 ? (
            profileData.businesses.map((business: any, index: number) => (
              <div key={index} className="profile-section">
                <h2 className="section-title">{tDashboard('profile.companyInformation')} {index + 1}</h2>
                <div className="profile-fields">
                  <div className="profile-field">
                    <label>{tDashboard('profile.companyName')}</label>
                    <div className="field-value">{business.businessName || tDashboard('profile.notProvided')}</div>
                  </div>
                  
                  <div className="profile-field">
                    <label>{tDashboard('profile.companyCategory')}</label>
                    <div className="field-value">
                      {(() => {
                        const cats = business.businessCategory;
                        if (Array.isArray(cats) && cats.length > 0) {
                          return cats.map((c: string) => tBusinessCategory(c) || c).join(', ');
                        }
                        if (typeof cats === 'string' && cats.trim()) {
                          return tBusinessCategory(cats) || cats;
                        }
                        return tDashboard('profile.notProvided');
                      })()}
                    </div>
                  </div>
                  
                  <div className="profile-field">
                    <label>{tDashboard('profile.countriesOfOperation')}</label>
                    <div className="field-value">
                      {business.countriesOfOperation && business.countriesOfOperation.length > 0 ? (
                        business.countriesOfOperation.map(country => tDashboard(`profile.countries.${country}`)).join(', ')
                      ) : tDashboard('profile.notSpecified')}
                    </div>
                  </div>
                  
                  <div className="profile-field">
                    <label>{tDashboard('profile.companyWebsite')}</label>
                    <div className="field-value">
                      {business.businessWebsite ? (
                        <a href={business.businessWebsite} target="_blank" rel="noopener noreferrer">
                          {business.businessWebsite}
                        </a>
                      ) : tDashboard('profile.noWebsite')}
                    </div>
                  </div>

                  <div className="profile-field">
                    <label>{tDashboard('profile.companyPhoneNumber')}</label>
                    <div className="field-value">
                      {(() => {
                        let cc = (business.companyPhoneCountryCode || "").replace(/[\s\-().]/g, "").trim();
                        let pn = (business.companyPhoneNumber || "").replace(/[\s\-().]/g, "").trim();
                        if (!pn) return tDashboard('profile.notProvided');
                        if (!cc) cc = "+44";
                        if (!cc.startsWith("+")) cc = `+${cc}`;
                        const ccDigits = cc.replace("+", "");
                        if (pn.startsWith(cc)) pn = pn.substring(cc.length);
                        else if (pn.startsWith(`+${ccDigits}`)) pn = pn.substring(ccDigits.length + 1);
                        else if (pn.startsWith(ccDigits) && pn.length > ccDigits.length + 4) pn = pn.substring(ccDigits.length);
                        if (cc === "+44" && pn.startsWith("0")) pn = pn.substring(1);
                        return pn ? `${cc} ${pn}` : tDashboard('profile.notProvided');
                      })()}
                    </div>
                  </div>
                  
                  <div className="profile-field">
                    <label>{tDashboard('profile.companyDescription')}</label>
                    <div className="field-value">{business.businessDescription || tDashboard('profile.notProvided')}</div>
                  </div>
                  
                  <div className="profile-field">
                    <label>{tDashboard('profile.companySocialMedia')}</label>
                    <div className="field-value">{business.socialMedia || tDashboard('profile.notProvided')}</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="profile-section">
              <h2 className="section-title">{tDashboard('profile.companyInformation')}</h2>
              <div className="profile-fields">
                <div className="profile-field">
                  <label>{tDashboard('profile.companyName')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.companyCategory')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.countriesOfOperation')}</label>
                  <div className="field-value">{tDashboard('profile.notSpecified')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.companyWebsite')}</label>
                  <div className="field-value">{tDashboard('profile.noWebsite')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.companyDescription')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.companySocialMedia')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Interests Section - Second, matches questionnaire */}
          <div className="profile-section">
            <h2 className="section-title">{tDashboard('profile.interests')}</h2>
            <div className="profile-fields">
              <div className="profile-field">
                <div className="field-value">
                  {profileData.interestedIn && profileData.interestedIn.length > 0 ? (
                    translatePosition(profileData.interestedIn, tDashboard, "profile.interestedInOptions", tDashboardExists)
                  ) : tDashboard('profile.notSpecified')}
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information Section - Third, matches questionnaire */}
          <div className="profile-section">
            <h2 className="section-title">{tDashboard('profile.personalInformation')}</h2>
            <div className="profile-fields">
              <div className="profile-field">
                <label>{tDashboard('profile.name')}</label>
                <div className="field-value">{profileData.name || tDashboard('profile.notProvided')}</div>
              </div>
              
              <div className="profile-field">
                <label>{tDashboard('profile.surname')}</label>
                <div className="field-value">{profileData.surname || tDashboard('profile.notProvided')}</div>
              </div>
              
              <div className="profile-field">
                <label>{tDashboard('profile.phoneNumber')}</label>
                <div className="field-value">
                  {(() => {
                    let cc = (profileData.countryCode || "").replace(/[\s\-().]/g, "").trim();
                    let pn = (profileData.phoneNumber || "").replace(/[\s\-().]/g, "").trim();
                    if (!pn) return tDashboard('profile.notProvided');
                    if (!cc) cc = "+44";
                    if (!cc.startsWith("+")) cc = `+${cc}`;
                    const ccDigits = cc.replace("+", "");
                    if (pn.startsWith(cc)) pn = pn.substring(cc.length);
                    else if (pn.startsWith(`+${ccDigits}`)) pn = pn.substring(ccDigits.length + 1);
                    else if (pn.startsWith(ccDigits) && pn.length > ccDigits.length + 4) pn = pn.substring(ccDigits.length);
                    if (cc === "+44" && pn.startsWith("0")) pn = pn.substring(1);
                    return pn ? `${cc} ${pn}` : tDashboard('profile.notProvided');
                  })()}
                </div>
              </div>

              <div className="profile-field">
                <label>{tDashboard('profile.email')}</label>
                <div className="field-value">{profileData.email || tDashboard('profile.notProvided')}</div>
              </div>
              
              <div className="profile-field">
                <label>{tDashboard('profile.position')}</label>
                <div className="field-value">
                  {translatePosition(profileData.position, tDashboard, "profile.positionOptions", tDashboardExists) || tDashboard('profile.notProvided')}
                </div>
              </div>
              
              <div className="profile-field">
                <label>{tDashboard('profile.linkedin')}</label>
                <div className="field-value">
                  {profileData.linkedin === 'no-linkedin' 
                    ? tDashboard('profile.noLinkedIn')
                    : profileData.linkedin || tDashboard('profile.notProvided')}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default B2BProfile;
